import { useEffect, useState } from "react";
import { isIOS, isAndroid } from "react-device-detect";
import { QRPopup, STORE_URLS } from "@/utils/app-download-button";
import { useScrollReveal } from "@/hooks/useScrollReveal";
import { ACTIVE_URL } from "@/constants";

interface NovaHeroProps {
  showQR: boolean;
  setShowQR: (v: boolean) => void;
}

export function NovaHero({ showQR, setShowQR }: NovaHeroProps) {
  const isOnMobile = isIOS || isAndroid;
  const mobileUrl = isIOS ? STORE_URLS.ios : STORE_URLS.android;
  const { ref: textRef, visible: textVisible } = useScrollReveal(0.05);
  const { ref: imgRef, visible: imgVisible } = useScrollReveal(0.05);
  // Admin-managed header image ("nova-header" PageImage); seeded by backend migration
  const [headline, setHeadline] = useState("Με τη Nova,\nκερδίζεις PADU!");
  const [headlineHtml, setHeadlineHtml] = useState<string | null>(null);
  const [headerImage, setHeaderImage] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetch(`${ACTIVE_URL}/api/content/nova-hero/`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!cancelled && data) {
          if (data.title) setHeadline(data.title);
          if (data.content) setHeadlineHtml(data.content);
        }
      })
      .catch(() => {});
    fetch(`${ACTIVE_URL}/api/page-image/nova-header/`)
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (!cancelled && data?.image_url) setHeaderImage(data.image_url); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  function renderHeadline(text: string) {
    if (!text.includes("Nova") || !text.includes("PADU")) {
      return text;
    }

    const [beforeNova, afterNova = ""] = text.split("Nova");
    const [beforePadu, afterPadu = ""] = afterNova.split("PADU");

    return (
      <>
        {beforeNova}
        <span className="text-[#00d9d9]">Nova</span>
        {beforePadu}
        <span className="text-[#00d9d9]">PADU</span>
        {afterPadu}
      </>
    );
  }

  const headlineContent = headlineHtml ? (
    <div
      className="inline-block text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 font-roboto"
      style={{ whiteSpace: "pre-line" }}
      dangerouslySetInnerHTML={{ __html: headlineHtml }}
    />
  ) : (
    <h1 className="text-5xl lg:text-6xl font-bold text-white leading-tight mb-4 font-roboto" style={{ whiteSpace: "pre-line" }}>
      {renderHeadline(headline)}
    </h1>
  );

  return (
    <section className="max-w-[1200px] mx-auto px-6 pb-30 flex flex-col lg:flex-row items-center gap-12 pt-28">
      <div
        ref={textRef}
        className="flex-1 text-center lg:text-left transition-all duration-700"
        style={{
          opacity: textVisible ? 1 : 0,
          transform: textVisible ? 'translateY(0)' : 'translateY(32px)',
        }}
      >
        {headlineContent}
        <p className="text-white/60 text-lg leading-relaxed max-w-xl mb-8">
          Απόλαυσε exclusive Nova deals & αμέτρητες προσφορές σε πάνω από{" "}
          <span className="text-white font-semibold">1.000 σημεία</span> σε όλη την Ελλάδα.
        </p>

        <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
          {isOnMobile ? (
            <a href={mobileUrl} target="_blank" rel="noopener noreferrer">
              <button className="hover:scale-105 transform transition-all duration-200 cursor-pointer font-bold px-10 py-3.5 rounded-full bg-[#00d9d9] text-black text-sm font-press-start whitespace-nowrap">
                Κατέβασε την Εφαρμογή
              </button>
            </a>
          ) : (
            <button
              onClick={() => setShowQR(true)}
              className="hover:scale-105 transform transition-all duration-200 cursor-pointer font-bold px-10 py-3.5 rounded-full bg-[#00d9d9] text-black text-sm font-press-start whitespace-nowrap"
            >
              Κατέβασε την Εφαρμογή
            </button>
          )}
          <a
            href="#perks"
            className="hover:scale-105 transform transition-all duration-200 border border-white/20 text-white/80 hover:text-white hover:border-white/40 font-semibold px-10 py-3.5 rounded-full text-sm text-center"
          >
            Δες τα οφέλη
          </a>
        </div>
      </div>

      <div
        ref={imgRef}
        className="flex-1 flex justify-center lg:justify-end transition-all duration-700"
        style={{
          opacity: imgVisible ? 1 : 0,
          transform: imgVisible ? 'translateY(0) scale(1)' : 'translateY(24px) scale(0.97)',
          transitionDelay: '150ms',
        }}
      >
        {headerImage && (
          <img src={headerImage} alt="Nova × PADU" className="w-[480px] h-[480px] object-cover" />
        )}
      </div>

      {showQR && <QRPopup onClose={() => setShowQR(false)} />}
    </section>
  );
}
